import React from "react";
//#region types
import { ActionButton, PageFooterProps } from "models/propTypes/commonPropTypes";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { HTMLConstants, IconDefinitionConstants } from "utils/constants";
import useFloatingMessages from "./messages/useFloatingMessages";
import useNavigationHistory from "./useNavigationHistory";
import GridR from "./grid/responsiveGrid";
import RoundButton from "./buttons/roundButton";
import Icon from "./icon";
import StyledButton from "./buttons/styledButton";
import MoreActionsButton from "./buttons/moreActionsButton";
import SupportSpeedDial from "components/support/supportSpeedDial";
import useCheckRelations from "./useCheckRelations";
import SimpleTooltip from "./tooltips/tooltip";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance } from "utils/helpers";

const PageFooter: React.FC<PageFooterProps> = (props) => {
  const {
    actions,
    allowForReadOnlyUsers,
    moreActions,
    onActionSuccess,
    columnsLeft,
    showNavigateBack,
    showHelp,
    onBackButtonClick,
    onNextButtonClick,
    isFormValid,
    disableSuccessMessages,
  } = props;
  const [submittingBtn, setSubmittingButton] = React.useState<number | undefined>();

  const { getIsReadonlyUser } = useCheckRelations();
  const isReadonlyUser = getIsReadonlyUser();

  const scrollErrorMessagesIntoView = () => {
    const messageContainerId = HTMLConstants.MessagesId;
    const errorMessageContainer = document.getElementById(messageContainerId);
    errorMessageContainer?.scrollIntoView({ behavior: "smooth" });
  };

  const { enqueueMessage } = useFloatingMessages();
  const onButtonClick = (btnIndex: number, action: ActionButton) => {
    const { onClick: clickFunc, successMessage, onActionSuccess: onButtonActionSuccess } = action;

    if (clickFunc) {
      setSubmittingButton(btnIndex);
      clickFunc()
        .then((result) => {
          (onButtonActionSuccess && onButtonActionSuccess(result)) || (onActionSuccess && onActionSuccess(result));
          if (!disableSuccessMessages && successMessage) {
            enqueueMessage(
              LocalizationHelperInstance.getFormattedMessage(successMessage, successMessage),
              StyleEnums.DisplayType.Success
            );
          }
        })
        .catch((error) => {
          scrollErrorMessagesIntoView();
          (action.onActionError && action.onActionError(error)) || (props.onActionError && props.onActionError(error));
        })
        .finally(() => {
          setSubmittingButton(undefined);
        });
    }
  };

  const { goBack } = useNavigationHistory();

  return (
    <GridR container className={clsx(cssExports.pageFooterActionsContainer, props.className)} data-testid="pageFooter">
      {columnsLeft ? <GridR item md={columnsLeft}></GridR> : <></>}
      <GridR container item justifyContent="center" alignItems="center" spacing={2} direction="row" wrap="nowrap">
        {showHelp ? <SupportSpeedDial /> : <></>}
        <GridR
          container
          item
          justifyContent="center"
          alignItems="center"
          spacing={2}
          direction="row"
          wrap="nowrap"
          className={cssExports.pageFooterActions}
        >
          {(showNavigateBack || onBackButtonClick) && (
            <GridR item className={cssExports.navigationBackButton}>
              <RoundButton className={clsx(cssExports.white)} onClick={onBackButtonClick ?? goBack} testId="backButton">
                <Icon icon={IconDefinitionConstants.WChevronLeft} />
              </RoundButton>
            </GridR>
          )}
          {moreActions && (
            <GridR item className={cssExports.moreActionsButton}>
              {
                <moreActions.type
                  key={moreActions.key}
                  {...moreActions.props}
                  isFormValid={isFormValid}
                  disableSuccessMessages={props.disableSuccessMessages}
                  testId="moreActionsButton"
                />
              }
            </GridR>
          )}
          {(allowForReadOnlyUsers || !isReadonlyUser) &&
            actions.map((action, index) => (
              <GridR key={`footer-action-${index}`} item className={cssExports.actionButtons}>
                {action.moreActions === undefined || action.moreActions.length === 0 ? (
                  <SimpleTooltip text={action.tooltipText} hidden={!action.showTooltip}>
                    <div>
                      <StyledButton
                        displayType={action.type}
                        disabled={action.isDisabled}
                        onClick={onButtonClick.bind(null, index, action)}
                        isSubmitting={submittingBtn !== undefined && submittingBtn === index}
                        secondary={action.secondary}
                        testId={action.testId}
                      >
                        {typeof action.text === "string"
                          ? LocalizationHelperInstance.getFormattedMessage(action.text, action.text)
                          : action.text}
                      </StyledButton>
                    </div>
                  </SimpleTooltip>
                ) : (
                  <MoreActionsButton
                    onClick={onButtonClick.bind(null, index, action)}
                    isSubmitting={!!submittingBtn && submittingBtn === index}
                    menuItems={action.moreActions}
                    onActionSuccess={onActionSuccess}
                    disabled={submittingBtn !== undefined || action.isDisabled}
                    displayType={StyleEnums.DisplayType.Success}
                    testId={action.testId}
                  >
                    {typeof action.text === "string"
                      ? LocalizationHelperInstance.getFormattedMessage(action.text, action.text)
                      : action.text}
                  </MoreActionsButton>
                )}
              </GridR>
            ))}
          {onNextButtonClick && (
            <GridR item className={cssExports.navigationBackButton}>
              <RoundButton className={clsx(cssExports.white)} onClick={onNextButtonClick} testId="nextButton">
                <Icon icon={IconDefinitionConstants.WChevronRight} />
              </RoundButton>
            </GridR>
          )}
        </GridR>
      </GridR>
    </GridR>
  );
};

export default PageFooter;
